import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import {
  STEP_SECTION_HEADINGS,
  type SectionHeadingDef,
} from '../../../Campaigns/guided/config/sectionHeadings';

/** DB-authored content for a step, persisted to `metadata.runner`. */
export interface RunnerContentValues {
  tag: string;
  title: string;
  subtitle: string;
  cta: string;
  liveEligible: boolean;
  /** One quick-prompt per entry; edited as newline-separated text. */
  quickPrompts: string[];
  /** Propose only: heading + description for the "Your conversation guide" block. */
  guideTitle: string;
  guideBody: string;
  /** In-step section header overrides, keyed by section id (see STEP_SECTION_HEADINGS). */
  sectionHeadings: Record<string, { title?: string; lead?: string }>;
}

export const emptyRunnerContent = (): RunnerContentValues => ({
  tag: '',
  title: '',
  subtitle: '',
  cta: '',
  liveEligible: false,
  quickPrompts: [],
  guideTitle: '',
  guideBody: '',
  sectionHeadings: {},
});

/** Read a step's `metadata.runner` into the form shape (tolerant of missing/typed-wrong fields). */
export const readRunnerContent = (raw: unknown): RunnerContentValues => {
  const f = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const sectionHeadings: Record<string, { title?: string; lead?: string }> = {};
  if (f.sectionHeadings && typeof f.sectionHeadings === 'object') {
    for (const [id, v] of Object.entries(f.sectionHeadings as Record<string, unknown>)) {
      if (v && typeof v === 'object') {
        const o = v as Record<string, unknown>;
        sectionHeadings[id] = {
          title: typeof o.title === 'string' ? o.title : '',
          lead: typeof o.lead === 'string' ? o.lead : '',
        };
      }
    }
  }

  return {
    tag: typeof f.tag === 'string' ? f.tag : '',
    title: typeof f.title === 'string' ? f.title : '',
    subtitle: typeof f.subtitle === 'string' ? f.subtitle : '',
    cta: typeof f.cta === 'string' ? f.cta : '',
    liveEligible: f.liveEligible === true,
    quickPrompts: Array.isArray(f.quickPrompts)
      ? (f.quickPrompts.filter((p) => typeof p === 'string') as string[])
      : [],
    guideTitle: typeof f.guideTitle === 'string' ? f.guideTitle : '',
    guideBody: typeof f.guideBody === 'string' ? f.guideBody : '',
    sectionHeadings,
  };
};

/**
 * Populate a step's *effective* section-heading defaults into the form values so the author sees (and,
 * on save, persists) the real starting copy instead of an empty box — the "no blank 'leave for default'
 * fields; DB-driven, not code-fallback" pass.
 *
 * It fills ONLY genuinely-blank fields, so an already-authored value is never overwritten. Two things
 * make this parity-safe for the preserved Frontier campaign: (1) migration 0166 already seeds every
 * frontier step's `sectionHeadings` with these exact literals, so the blank-only guard makes this a
 * no-op there — its render is unchanged; (2) it is applied only at the seed of an edit form and
 * persisted on an explicit Save, never on open.
 *
 * It deliberately skips the two fields whose runtime default is *derived* rather than a static literal,
 * because materializing them would freeze a value that is meant to track its source:
 *   - a section title flagged `titleDerived` (the runner derives it, e.g. from a deck label), and
 *   - the conversation-guide heading/description (`guideTitle`/`guideBody`), which fall back through the
 *     step title/subtitle before any literal — and which no migration seeds.
 */
export const materializeRunnerDefaults = (
  values: RunnerContentValues,
  kind?: string,
): RunnerContentValues => {
  const defs: SectionHeadingDef[] | undefined = kind
    ? (STEP_SECTION_HEADINGS as any)[kind]
    : undefined;
  if (!Array.isArray(defs) || defs.length === 0) return values;
  const sectionHeadings = { ...values.sectionHeadings };
  for (const def of defs) {
    const current = sectionHeadings[def.id] ?? {};
    const next = { ...current };
    if (!(current.title ?? '').trim() && !def.titleDerived && def.title) next.title = def.title;
    if (!(current.lead ?? '').trim() && !def.titleOnly && def.lead) next.lead = def.lead;
    sectionHeadings[def.id] = next;
  }
  return { ...values, sectionHeadings };
};

/**
 * Authoring panel for a step's presentation content (title / subtitle / tag / CTA / present-live /
 * Guide quick-prompts / section headings), persisted to `metadata.runner`. Purely controlled: binds
 * nested `runner.*` names on the parent form's `control`.
 */
export function RunnerContentFields(props: {
  control: Control<any>;
  disabled?: boolean;
  /** The step's kind, used to pick which section-heading fields to expose. */
  kind?: string;
}): JSX.Element {
  const { control, disabled, kind } = props;
  const sectionDefs: SectionHeadingDef[] | undefined = kind
    ? (STEP_SECTION_HEADINGS as any)[kind]
    : undefined;
  return (
    <Stack spacing={1.5} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Step content
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The headline copy shown at the top of this step.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="runner.tag"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Phase tag"
              size="small"
              disabled={disabled}
              helperText="Short eyebrow label (e.g. Profiling)"
              sx={{ minWidth: 200 }}
            />
          )}
        />
        <Controller
          name="runner.cta"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Continue button label"
              size="small"
              fullWidth
              disabled={disabled}
              helperText="Standalone runner only; the portal supplies its own Next."
            />
          )}
        />
      </Stack>

      <Controller
        name="runner.title"
        control={control}
        render={({ field }) => (
          <TextField {...field} label="Title" size="small" fullWidth disabled={disabled} />
        )}
      />

      <Controller
        name="runner.subtitle"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Subtitle"
            size="small"
            multiline
            minRows={2}
            fullWidth
            disabled={disabled}
          />
        )}
      />

      <Controller
        name="runner.guideTitle"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Conversation guide — heading"
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Your conversation guide"
            helperText="Propose step only. Blank falls back to this step’s Title, then “Your conversation guide”."
          />
        )}
      />

      <Controller
        name="runner.guideBody"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Conversation guide — description"
            size="small"
            multiline
            minRows={2}
            fullWidth
            disabled={disabled}
            placeholder="This section has the conversation guide to help you lead the discussion — it is your reference, not something to hand over as-is."
            helperText="Propose step only. Blank falls back to this step’s Subtitle, then the standard guidance copy shown here."
          />
        )}
      />

      {Array.isArray(sectionDefs) && sectionDefs.length > 0 && (
        <Stack spacing={1.5} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Section headings
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The headings shown inside this step, prefilled with their defaults — edit any to override.
          </Typography>
          {sectionDefs.map((def) => (
            <React.Fragment key={def.id}>
              <Controller
                name={`runner.sectionHeadings.${def.id}.title`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={`${def.label} — title`}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={(field.value as string) ?? ''}
                    placeholder={def.title}
                    helperText={
                      def.titleDerived
                        ? 'Blank uses the runner’s derived title (e.g. the deck label).'
                        : 'Prefilled with the default — edit to override, or blank to hide the title.'
                    }
                  />
                )}
              />
              {!def.titleOnly && (
                <Controller
                  name={`runner.sectionHeadings.${def.id}.lead`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`${def.label} — description`}
                      size="small"
                      multiline
                      minRows={2}
                      fullWidth
                      disabled={disabled}
                      value={(field.value as string) ?? ''}
                      placeholder={def.lead}
                      helperText="Prefilled with the default — edit to override, or blank to hide the description."
                    />
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </Stack>
      )}

      <Controller
        name="runner.quickPrompts"
        control={control}
        render={({ field }) => (
          <TextField
            label="Guide quick prompts"
            size="small"
            multiline
            minRows={3}
            fullWidth
            disabled={disabled}
            helperText="One prompt per line; shown as suggested questions in the Workshop Guide."
            value={(field.value as string[] | undefined)?.join('\n') ?? ''}
            onChange={(e) =>
              field.onChange(
                e.target.value.split('\n').map((s) => s.replace(/\r$/, '')),
              )
            }
          />
        )}
      />

      <Controller
        name="runner.liveEligible"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                disabled={disabled}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Can present full-screen (Go Live)
                </Box>
                <Box
                  component="span"
                  sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Shows the “Go Live: present full-screen” control so the SE can present this step to
                  the customer.
                </Box>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        )}
      />
    </Stack>
  );
}

export default RunnerContentFields;
